import { Globe, Lightning, Users } from '@phosphor-icons/react';
import type { CSSProperties } from 'react';
import { CATEGORIE_DE_REPLI, type EventCategory, type SiroccoEvent } from '@sirocco/shared';
import { t } from '../../i18n';

/**
 * L'etat d'un evenement, dit en une ligne.
 *
 * Trois choses a faire passer, et un seul endroit pour les ecrire : la
 * carte du fil, la carte de la liste et la fiche disent exactement la
 * meme phrase, avec les memes mots.
 *
 * - **Le seuil**, qui ne se dit pas ici mais a cote du nombre de
 *   participants : « 3/10 participants (5 au min.) ». Une phrase entiere
 *   pour dire ce qu'un chiffre suffit a porter faisait un bandeau de plus
 *   sur une carte qui en a deja.
 * - **La confirmation**, qui n'a de sens que si un seuil etait annonce :
 *   sans seuil, un evenement est confirme des sa creation et le dire ne
 *   dirait rien.
 * - **L'annulation**, qui n'est pas un etat range en base mais une
 *   lecture de l'heure : le debut est passe, le compte n'y etait pas.
 *   Rien a tenir a jour, donc rien qui puisse se desynchroniser.
 */
export function EventEtat({ event }: { event: SiroccoEvent }) {
  // « En attente » se lit dans le compte des participants, pas ici.
  if (!event.seuil || event.statut === 'attente') return null;

  if (event.statut === 'annule') {
    return (
      <span className="ev-etat ev-etat--annule">
        <Users size={14} />
        {t.composants.etat.annule}
      </span>
    );
  }

  if (event.statut === 'confirme') {
    return (
      <span className="ev-etat ev-etat--confirme">
        <Users size={14} />
        {t.composants.etat.confirme}
      </span>
    );
  }

  return null;
}

/**
 * La pastille des sorties spontanees.
 *
 * Une sortie proposee pour ce soir et un atelier prepare depuis un mois
 * n'appellent pas la meme attention ; ils se ressemblaient pourtant trait
 * pour trait dans la liste. La distinction se fait ici, pas dans un
 * onglet a part : separer les deux listes aurait vide les deux.
 */
export function EventSpontane({ event }: { event: SiroccoEvent }) {
  if (!event.spontane) return null;
  return (
    <span className="ev-spontane">
      <Lightning size={11} weight="fill" />
      {t.composants.etat.spontanee}
    </span>
  );
}

/**
 * La pastille des evenements en ligne, en couleur comme « Inscrit(e) » :
 * c'est en parcourant la liste qu'on veut savoir qu'on n'aura pas a se
 * deplacer, pas une fois la fiche ouverte.
 */
export function EventEnLigne({ event }: { event: SiroccoEvent }) {
  if (!event.enLigne) return null;
  return (
    <span className="ev-en-ligne">
      <Globe size={12} weight="bold" />
      {t.composants.etat.enLigne}
    </span>
  );
}

/**
 * Le seuil, dit en trois mots a cote du compte des participants.
 *
 * « (5 au min.) » se lit d'un coup d'oeil et se range la ou l'on regarde
 * deja pour savoir si la sortie se remplit.
 */
export function EventSeuil({ event }: { event: SiroccoEvent }) {
  if (!event.seuil || event.statut === 'annule') return null;
  return <span className="ev-card__min">{t.composants.etat.seuil(event.seuil)}</span>;
}

/**
 * La scene de chaque categorie, quand la sortie n'a pas d'affiche.
 *
 * Des illustrations en couleur, recadrees en carre a 264 px (la vignette
 * en fait 88, a 3x) et en WebP : six fichiers de 4 a 10 Ko. Elles
 * remplacent le pochoir la ou elles existent ; une categorie sans photo
 * garde le dessin de la plus proche (voir plus bas).
 *
 * Jeux en a deux : le plateau, et pour les sorties en ligne l'ecran — la
 * partie a distance ne se dit pas par un plateau. Celle du plateau porte
 * le filigrane de la banque d'images d'ou elle vient, posee telle quelle
 * a la demande d'Idris.
 */
const PHOTOS: Partial<Record<EventCategory, string>> = {
  sport: '/evenements/sport.webp',
  culture: '/evenements/culture.webp',
  pro: '/evenements/pro.webp',
  food: '/evenements/food.webp',
  etudes: '/evenements/etudes.webp',
  jeux: '/evenements/jeux.webp',
};

const PHOTOS_EN_LIGNE: Partial<Record<EventCategory, string>> = {
  jeux: '/evenements/jeux-en-ligne.webp',
};

/**
 * L'affiche d'un evenement, la photo de sa categorie, ou son dessin.
 *
 * Une sortie sans affiche ne doit pas etre un rectangle vide a cote de
 * celles qui en ont une : la categorie, elle, est obligatoire, donc il y
 * a toujours quelque chose a montrer. C'est aussi ce qui donne a la liste
 * son rythme — quatre visuels alignes se parcourent plus vite que quatre
 * pavés de texte.
 *
 * **Le dessin de categorie n'est pas une image, c'est un pochoir.** Les
 * quatre illustrations sont noires sur blanc ; posees telles quelles,
 * elles plaquaient un carre blanc au milieu d'une carte sombre et un
 * trait noir qui n'appartenait a aucun spot. Leurs fichiers ne portent
 * donc plus que de l'alpha — le trait opaque, le fond transparent — et
 * c'est la feuille de style qui les colore. La couleur suit alors le spot
 * et l'heure sans que rien ne soit redessine : rose a Berlin la nuit,
 * terre cuite a Marrakech le jour.
 */
export function EventAffiche({ event }: { event: SiroccoEvent }) {
  const photo =
    event.imageUrl ??
    (event.enLigne ? PHOTOS_EN_LIGNE[event.category] : undefined) ??
    PHOTOS[event.category];
  if (photo) {
    return (
      <span className="ev-card__affiche">
        <img src={photo} alt="" loading="lazy" />
      </span>
    );
  }

  /*
    Sans photo, le dessin : seules les quatre categories d'origine en ont
    un, les autres prennent celui de la plus proche (Asso la culture) : un
    dessin qui manque laissait un cadre vide.
  */
  return (
    <span className="ev-card__affiche ev-card__affiche--dessin">
      <span
        className="ev-dessin"
        style={{ '--dessin': `url(/evenements/${CATEGORIE_DE_REPLI[event.category]}.png)` } as CSSProperties}
      />
    </span>
  );
}
